import { QueryKeys, SocialLoginUrls } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { IAddToCartItem, IAddToCartItemAttribute, IGetCartProduct } from '@/interfaces/cart';
import { addToCart } from '@/services/cart.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { signOut } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { REDIRECT_BY_RTK_ERROR_STRING, SESSION_STALE_TIME } from '../constants';
import { ICustomAuthHookModel, INextAuthSessionModel, IUseSessionModel } from '../interfaces/authentication';
import { getSessionFromNextServer } from '../services/authentication.service';
import { clearAllCookies } from '@/utils/helper';

export async function fetchSession(queryParamString?: string): Promise<INextAuthSessionModel | null> {
  const session = (await getSessionFromNextServer(queryParamString)).data;
  if (Object.keys(session).length) {
    return session;
  }
  return null;
}
// const { status } = useSession();
export function useReactQuerySession({
  required = false,
  redirectTo = '/?error=' + REDIRECT_BY_RTK_ERROR_STRING,
  queryConfig = {},
}: IUseSessionModel = {}): ICustomAuthHookModel {
  const queryClient = useQueryClient();
  const [loading, isLoading] = useState(false);
  if (!queryConfig.staleTime) {
    queryConfig.staleTime = SESSION_STALE_TIME; // 50 min, during this, this data will be fresh
  }
  queryConfig.refetchIntervalInBackground = true;
  queryConfig.onSettled = (data, error) => {
    if (data || !required) return;
    router.push(redirectTo);
  };
  const router = useRouter();
  const query = useQuery<INextAuthSessionModel | null>(['session'], () => fetchSession(), {
    ...queryConfig,
  });
  useEffect(() => {
    if (query.data?.error === 'RefreshAccessTokenError') {
      // console.log('invoking refresh token');
      signOut({ callbackUrl: '/', redirect: true }).then(() => {
        clearAllCookies();
      });
      if (window.history?.state?.as === SocialLoginUrls.GoogleLogin || window.history.state?.as?.includes(SocialLoginUrls.FacebookLogin)) {
        window.close();
      }
    } else if (query.data?.accessToken) {
      let stringifiedCartItems = localStorage.getItem('cart');
      if (stringifiedCartItems) {
        console.log('invoking cart with localstorage items');
        try {
          //  if (!loading) {
          let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
          if (tempCartItems.Items) {
            let cartItems: IGetCartProduct = tempCartItems;
            cartItems.Items.forEach((cartItem) => {
              console.log(cartItem, 'from local Storage');
              let tempAttributes: IAddToCartItemAttribute[] = [];
              let selectedVariant = cartItem.Variations.find((x) => x.IsSelected);
              if (selectedVariant) {
                tempAttributes.push({
                  key: selectedVariant.ProductAttributeId,
                  value: selectedVariant.ProductAttributeValueId,
                });
              }
              const payload: IAddToCartItem = {
                productId: cartItem.ProductId,
                quantity: cartItem.Quantity,
                attributes: tempAttributes,
              };
              isLoading(true);
              addToCart(payload, query.data)
                .then((x) => {
                  console.log(x, 'done');
                  queryClient.invalidateQueries([QueryKeys.cartItems, query.data?.accessToken || 'anonymous']);
                  queryClient.invalidateQueries([QueryKeys.orderSummary]);
                })
                .catch((data: AxiosError) => {
                  console.log(data, 'error');
                  openNotification(
                    'danger',
                    'Add to cart failed.',
                    (data.response?.data as { Messages: string[] })?.Messages[0] ||
                      'Cart item sync encountered an error, please try adding the item again.',
                  );
                })
                .finally(() => {
                  localStorage.removeItem('cart');
                  if (
                    window.history?.state?.as === SocialLoginUrls.GoogleLogin ||
                    window.history.state?.as?.includes(SocialLoginUrls.FacebookLogin)
                  ) {
                    window.close();
                  }
                  isLoading(false);
                });
            });
            localStorage.removeItem('cart');
          } else {
            localStorage.removeItem('cart');
            if (window.history?.state?.as === SocialLoginUrls.GoogleLogin || window.history.state?.as?.includes(SocialLoginUrls.FacebookLogin)) {
              window.close();
            }
          }
          //  }
        } catch (e) {
          isLoading(false);
          console.log(e, 'json parse error');
          localStorage.removeItem('cart');
          if (window.history?.state?.as === SocialLoginUrls.GoogleLogin || window.history.state?.as?.includes(SocialLoginUrls.FacebookLogin)) {
            window.close();
          }
        }
      } else {
        // wait 3 seconds to close so the network call initiates
        setTimeout(() => {
          if (window.history?.state?.as === SocialLoginUrls.GoogleLogin || window.history?.state?.as?.includes(SocialLoginUrls.FacebookLogin)) {
            window.close();
          }
        }, 2000);
      }
    }
  }, [query.data]);

  let authObject = {
    // isAuthenticated: query.data ? (query.data?.error ? false : true) : false,
    isAuthenticated: query.data ? true : false,
    session: query.data,
    loading: query.status === 'loading',
    addToCartLoading: loading,
  };
  return authObject;
}
